import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { ExpiresIn } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/ExpiresIn';
import { InventoryCounterInline } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/InventoryCounterInline';
import { ResetIn } from '@sorare/core/src/components/clubShop/ClubShopItem/Labels/ResetIn';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { isType } from '@sorare/core/src/gql';
import { glossary } from '@sorare/core/src/lib/glossary';

import { BuyButton } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/BuyButton';
import { useOnBuyButtonClick } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/BuyButton/useOnBuyButtonClick';
import { ConsumableCta } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/ConsumableCta';
import { Details } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/Details';
import { ItemImagePreview } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/ItemImagePreview';
import { Requirements } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog/Requirements';

import { RaffleContent_clubShopItem } from './__generated__/index.graphql';

const FlexContainer = styled(Vertical).attrs({ center: true })`
  justify-content: flex-start;
`;

type Props = {
  item: RaffleContent_clubShopItem;
  inventory: boolean;
  onBuy?: () => void;
  userCoinBalance: number;
  onClose: () => void;
};

export const RaffleContent = ({
  item,
  inventory,
  onBuy,
  userCoinBalance,
  onClose,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const [currentStep, setCurrentStep] = useState<'step1' | 'step2'>(
    inventory ? 'step2' : 'step1'
  );
  const { onBuyButtonClick, loading } = useOnBuyButtonClick(
    () => setCurrentStep('step2'),
    onBuy
  );

  if (!isType(item, 'RaffleShopItem')) {
    return null;
  }

  return (
    <>
      <ItemImagePreview
        pictureUrl={item.pictureUrl}
        name={item.name}
        type={item.position}
      />
      <Details
        inventory={inventory}
        item={item}
        displayTerms={currentStep === 'step1'}
      />
      {currentStep === 'step1' ? (
        <>
          <FlexContainer>
            {item.disabled && <Requirements item={item} />}
            {item.myLimitResetAt && (
              <ResetIn myLimitResetAt={item.myLimitResetAt} />
            )}
            {item.expirationDate && (
              <ExpiresIn expirationDate={item.expirationDate} />
            )}
            {!item.disabled && (
              <InventoryCounterInline
                name={item.name}
                amount={item.myBalance}
              />
            )}
          </FlexContainer>
          <BuyButton
            item={item}
            onClick={() => onBuyButtonClick(item.id)}
            loading={loading}
            userCoinBalance={userCoinBalance}
          />
        </>
      ) : (
        !!currentUser && (
          <Vertical center>
            {item.description !== undefined && (
              <LabelM as="p" color="var(--c-nd-400)" className="text-center">
                {item.description}
              </LabelM>
            )}

            {item.infoLink && (
              <Button color="secondary" size="compact" to={item.infoLink}>
                <FormattedMessage {...glossary.learnMore} />
              </Button>
            )}

            <ConsumableCta
              item={item}
              onClose={onClose}
              inventory={inventory}
            />
          </Vertical>
        )
      )}
    </>
  );
};

RaffleContent.fragments = {
  clubShopItem: gql`
    fragment RaffleContent_clubShopItem on ClubShopItem {
      ... on ShopItemInterface {
        id
        pictureUrl
        name
        position
        myLimitResetAt
        myBalance
        expirationDate
        infoLink
        description
      }
      ...Details_shopItem
      ...ConsumableCta_shopItem
      ...Requirements_clubShopItem
    }
    ${Details.fragments.shopItem}
    ${Requirements.fragments.clubShopItem}
    ${ConsumableCta.fragments.shopItem}
  ` as TypedDocumentNode<RaffleContent_clubShopItem>,
};
